import React, { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

const AdminHome = () => {
  const { user, token, type, logout, APIdomain } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("courses");

  // Course form state
  const [courseForm, setCourseForm] = useState({
    title: "",
    description: "",
    status: "In Progress",
    level: "Secondary",
  });

  // Video form state
  const [videoForm, setVideoForm] = useState({
    courseId: "",
    link: "",
    index: "",
    title: "",
    description: "",
  });

  // Edit states
  const [editingCourse, setEditingCourse] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);

  // Fetch courses on component mount
  useEffect(() => {
    const onMountFetchCourses = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${APIdomain}/api/courses`);
        const data = await response.json();
        setCourses(data.data);
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoading(false);
      }
    };

    if (type === "admin") {
      onMountFetchCourses();
    }
  }, [type, APIdomain]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${APIdomain}/api/courses`);
      const data = await response.json();
      setCourses(data.data);
    } catch (error) {
      console.error("Error fetching courses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async () => {
    try {
      const response = await fetch(`${APIdomain}/api/courses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(courseForm),
      });

      if (response.ok) {
        setCourseForm({
          title: "",
          description: "",
          status: "In Progress",
          level: "Secondary",
        });
        fetchCourses();
        alert("Course created successfully!");
      }
    } catch (error) {
      console.error("Error creating course:", error);
      alert("Error creating course");
    }
  };

  const handleAddVideo = async () => {
    try {
      const response = await fetch(
        `${APIdomain}/api/courses/${videoForm.courseId}/videos`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            videos: [
              {
                link: videoForm.link,
                index: parseInt(videoForm.index),
                title: videoForm.title,
                description: videoForm.description,
              },
            ],
          }),
        }
      );

      if (response.ok) {
        setVideoForm({
          courseId: "",
          link: "",
          index: "",
          title: "",
          description: "",
        });
        fetchCourses();
        alert("Video added successfully!");
      }
    } catch (error) {
      console.error("Error adding video:", error);
      alert("Error adding video");
    }
  };

  const handleUpdateCourse = async (courseId) => {
    try {
      const response = await fetch(`${APIdomain}/api/courses/${courseId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editingCourse),
      });

      if (response.ok) {
        setEditingCourse(null);
        fetchCourses();
        alert("Course updated successfully!");
      }
    } catch (error) {
      console.error("Error updating course:", error);
      alert("Error updating course");
    }
  };

  const handleUpdateVideo = async (courseId, videoId) => {
    try {
      const response = await fetch(
        `${APIdomain}/api/courses/videos/${courseId}/${videoId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(editingVideo),
        }
      );

      if (response.ok) {
        setEditingVideo(null);
        fetchCourses();
        alert("Video updated successfully!");
      }
    } catch (error) {
      console.error("Error updating video:", error);
      alert("Error updating video");
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (window.confirm("Are you sure you want to delete this course?")) {
      try {
        const response = await fetch(`${APIdomain}/api/courses/${courseId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          fetchCourses();
          alert("Course deleted successfully!");
        }
      } catch (error) {
        console.error("Error deleting course:", error);
        alert("Error deleting course");
      }
    }
  };

  const handleDeleteVideo = async (courseId, videoId) => {
    if (window.confirm("Are you sure you want to delete this video?")) {
      try {
        const response = await fetch(
          `${APIdomain}/api/courses/videos/${courseId}/${videoId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          fetchCourses();
          alert("Video deleted successfully!");
        }
      } catch (error) {
        console.error("Error deleting video:", error);
        alert("Error deleting video");
      }
    }
  };

  if (type !== "admin") {
    return (
      <div style={styles.container}>
        <h1>Access Denied</h1>
        <p>You need admin privileges to access this page.</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>Admin Dashboard</h1>
        <div style={styles.userInfo}>
          <span>Welcome, {user?.username}</span>
          <button onClick={logout} style={styles.logoutBtn}>
            Logout
          </button>
        </div>
      </div>

      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab("courses")}
          style={activeTab === "courses" ? styles.activeTab : styles.tab}
        >
          Manage Courses
        </button>
        <button
          onClick={() => setActiveTab("create")}
          style={activeTab === "create" ? styles.activeTab : styles.tab}
        >
          Create Course
        </button>
        <button
          onClick={() => setActiveTab("videos")}
          style={activeTab === "videos" ? styles.activeTab : styles.tab}
        >
          Add Videos
        </button>
      </div>

      {activeTab === "courses" && (
        <div style={styles.section}>
          <h2>All Courses</h2>
          {loading ? (
            <p>Loading courses...</p>
          ) : (
            <div style={styles.coursesList}>
              {courses.map((course) => (
                <div key={course._id} style={styles.courseCard}>
                  <div style={styles.courseHeader}>
                    <h3>{course.title}</h3>
                    <div>
                      <button
                        onClick={() => setEditingCourse(course)}
                        style={styles.editBtn}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCourse(course._id)}
                        style={styles.deleteBtn}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <p style={styles.courseDescription}>{course.description}</p>
                  <div style={styles.courseInfo}>
                    <span style={styles.badge}>Level: {course.level}</span>
                    <span style={styles.badge}>Status: {course.status}</span>
                    <span style={styles.badge}>
                      Videos: {course.videos?.length || 0}
                    </span>
                  </div>

                  {course.videos && course.videos.length > 0 && (
                    <div style={styles.videosList}>
                      <h4>Videos:</h4>
                      {course.videos.map((video) => (
                        <div key={video._id} style={styles.videoItem}>
                          <span>
                            {video.index}. {video.title}
                          </span>
                          <div>
                            <button
                              onClick={() =>
                                setEditingVideo({
                                  ...video,
                                  courseId: course._id,
                                })
                              }
                              style={styles.editBtn}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteVideo(course._id, video._id)
                              }
                              style={styles.deleteBtn}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "create" && (
        <div style={styles.section}>
          <h2>Create New Course</h2>
          <div style={styles.form}>
            <input
              type="text"
              placeholder="Course Title"
              value={courseForm.title}
              onChange={(e) =>
                setCourseForm({ ...courseForm, title: e.target.value })
              }
              required
              style={styles.input}
            />
            <textarea
              placeholder="Course Description"
              value={courseForm.description}
              onChange={(e) =>
                setCourseForm({ ...courseForm, description: e.target.value })
              }
              required
              style={styles.textarea}
            />
            <select
              value={courseForm.status}
              onChange={(e) =>
                setCourseForm({ ...courseForm, status: e.target.value })
              }
              style={styles.select}
            >
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </select>
            <select
              value={courseForm.level}
              onChange={(e) =>
                setCourseForm({ ...courseForm, level: e.target.value })
              }
              style={styles.select}
            >
              <option value="Secondary">Secondary</option>
              <option value="Undergraduate">Undergraduate</option>
            </select>
            <button onClick={handleCreateCourse} style={styles.submitBtn}>
              Create Course
            </button>
          </div>
        </div>
      )}

      {activeTab === "videos" && (
        <div style={styles.section}>
          <h2>Add Video to Course</h2>
          <div style={styles.form}>
            <select
              value={videoForm.courseId}
              onChange={(e) =>
                setVideoForm({ ...videoForm, courseId: e.target.value })
              }
              required
              style={styles.select}
            >
              <option value="">Select Course</option>
              {courses.map((course) => (
                <option key={course._id} value={course._id}>
                  {course.title}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Video Title"
              value={videoForm.title}
              onChange={(e) =>
                setVideoForm({ ...videoForm, title: e.target.value })
              }
              required
              style={styles.input}
            />
            <input
              type="text"
              placeholder="Cloudflare Video Link"
              value={videoForm.link}
              onChange={(e) =>
                setVideoForm({ ...videoForm, link: e.target.value })
              }
              required
              style={styles.input}
            />
            <input
              type="number"
              placeholder="Video Index (for ordering)"
              value={videoForm.index}
              onChange={(e) =>
                setVideoForm({ ...videoForm, index: e.target.value })
              }
              required
              style={styles.input}
            />
            <textarea
              placeholder="Video Description"
              value={videoForm.description}
              onChange={(e) =>
                setVideoForm({ ...videoForm, description: e.target.value })
              }
              required
              style={styles.textarea}
            />
            <button onClick={handleAddVideo} style={styles.submitBtn}>
              Add Video
            </button>
          </div>
        </div>
      )}

      {/* Edit Course Modal */}
      {editingCourse && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2>Edit Course</h2>
            <h3>Course Title</h3>
            <input
              type="text"
              value={editingCourse.title}
              onChange={(e) =>
                setEditingCourse({ ...editingCourse, title: e.target.value })
              }
              style={styles.input}
            />
            <h3>Course Description</h3>
            <textarea
              value={editingCourse.description}
              onChange={(e) =>
                setEditingCourse({
                  ...editingCourse,
                  description: e.target.value,
                })
              }
              style={styles.textarea}
            />
            <h3>Course Status</h3>
            <select
              value={editingCourse.status}
              onChange={(e) =>
                setEditingCourse({ ...editingCourse, status: e.target.value })
              }
              style={styles.select}
            >
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </select>
            <h3>Course Level</h3>
            <select
              value={editingCourse.level}
              onChange={(e) =>
                setEditingCourse({ ...editingCourse, level: e.target.value })
              }
              style={styles.select}
            >
              <option value="Secondary">Secondary</option>
              <option value="Undergraduate">Undergraduate</option>
            </select>
            <div style={styles.modalButtons}>
              <button
                onClick={() => handleUpdateCourse(editingCourse._id)}
                style={styles.submitBtn}
              >
                Update
              </button>
              <button
                onClick={() => setEditingCourse(null)}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Video Modal */}
      {editingVideo && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2>Edit Video</h2>
            <h3>Video Title</h3>
            <input
              type="text"
              value={editingVideo.title}
              onChange={(e) =>
                setEditingVideo({ ...editingVideo, title: e.target.value })
              }
              style={styles.input}
            />
            <h3>Video Link</h3>
            <input
              type="text"
              value={editingVideo.link}
              onChange={(e) =>
                setEditingVideo({ ...editingVideo, link: e.target.value })
              }
              style={styles.input}
            />
            <h3>Video Index</h3>
            <input
              type="number"
              value={editingVideo.index}
              onChange={(e) =>
                setEditingVideo({
                  ...editingVideo,
                  index: parseInt(e.target.value),
                })
              }
              style={styles.input}
            />
            <h3>Video Description</h3>
            <textarea
              value={editingVideo.description}
              onChange={(e) =>
                setEditingVideo({
                  ...editingVideo,
                  description: e.target.value,
                })
              }
              style={styles.textarea}
            />
            <div style={styles.modalButtons}>
              <button
                onClick={() =>
                  handleUpdateVideo(editingVideo.courseId, editingVideo._id)
                }
                style={styles.submitBtn}
              >
                Update
              </button>
              <button
                onClick={() => setEditingVideo(null)}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "20px",
    fontFamily: "Arial, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
    paddingBottom: "20px",
    borderBottom: "2px solid #eee",
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },
  logoutBtn: {
    padding: "8px 16px",
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  tabs: {
    display: "flex",
    marginBottom: "30px",
    borderBottom: "1px solid #ddd",
  },
  tab: {
    padding: "12px 24px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    borderBottom: "2px solid transparent",
  },
  activeTab: {
    padding: "12px 24px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    borderBottom: "2px solid #007bff",
    color: "#007bff",
    fontWeight: "bold",
  },
  section: {
    marginBottom: "30px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    maxWidth: "500px",
  },
  input: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "16px",
    width: "100%",
  },
  textarea: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "16px",
    minHeight: "100px",
    resize: "vertical",
    width: "100%",
  },
  select: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontSize: "16px",
    width: "100%",
  },
  submitBtn: {
    padding: "12px 24px",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
  },
  coursesList: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  courseCard: {
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "20px",
    backgroundColor: "#f9f9f9",
  },
  courseHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },
  courseDescription: {
    color: "#666",
    marginBottom: "15px",
  },
  courseInfo: {
    display: "flex",
    gap: "10px",
    marginBottom: "15px",
  },
  badge: {
    padding: "4px 8px",
    backgroundColor: "#e9ecef",
    borderRadius: "4px",
    fontSize: "12px",
    color: "#495057",
  },
  editBtn: {
    padding: "6px 12px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    marginRight: "5px",
  },
  deleteBtn: {
    padding: "6px 12px",
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
  videosList: {
    marginTop: "15px",
  },
  videoItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px",
    backgroundColor: "white",
    borderRadius: "4px",
    marginBottom: "5px",
  },
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "8px",
    maxWidth: "500px",
    width: "90%",
    maxHeight: "80vh",
    overflowY: "auto",
  },
  modalButtons: {
    display: "flex",
    gap: "10px",
    marginTop: "20px",
  },
  cancelBtn: {
    padding: "12px 24px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
  },
};

export default AdminHome;
